/**
 * The shipped header, as a fill (RFC 0284 S3, S6c).
 *
 * The second slot to go through the registry, and the one that proves the first
 * was not a special case. The sidebar could be defended as "the big obvious
 * surface"; a strip along the top has a different shape, a different size and a
 * different reason to exist, and if the contract only fitted the sidebar it
 * would break here.
 *
 * WHAT THE HEADER IS TODAY. `SpaceTabs`, and only when `spacesAsTabs` is on
 * (RFC 0281 S5a). That condition moves INTO the built-in rather than staying in
 * the shell, because the shell is now asking a slot what to draw and must not
 * also know why the answer is sometimes nothing. A fill that decides it
 * has nothing to show renders nothing; that is a normal answer for chrome and
 * not a state anybody else has to model.
 *
 * WHY THE HEADER AND NOT THE PANEL NEXT. RFC 0273's side column is a LIST of
 * sections, so it is the one slot where "several fills" is the ordinary
 * case rather than the edge. Doing it second would have let the two-answer case
 * hide behind the many-answer case. The header is filled once, like the sidebar,
 * so what it tests is the SHAPE of a different surface and nothing else at the
 * same time.
 */

import type { ComponentProps } from 'react'
import { SpaceTabs } from '../../spaces/SpaceTabs'
import type { BuiltinFill } from '../slots'

/**
 * What the header slot is handed.
 *
 * The space verbs, and the preference that decides whether they are drawn along
 * the top at all. `spacesAsTabs` is the odd one: it is not something the header
 * needs in order to draw, it is the reason the header might draw nothing. It is
 * a prop rather than a check in the shell so that an INSTALLED header can ignore
 * it, which is the point — somebody who writes their own top strip should not
 * have it disappear because of a setting about our space tabs.
 */
export interface HeaderSlotProps extends ComponentProps<typeof SpaceTabs> {
  spacesAsTabs: boolean
}

export const BUILTIN_HEADER_ID = 'mod.spaces'

export const builtinHeader: BuiltinFill<'header'> = {
  id: BUILTIN_HEADER_ID,
  title: 'Space tabs',
  slot: 'header',
  builtin: true,
  render: ({ spacesAsTabs, ...props }) => {
    // NOTHING, not an empty strip. A header nobody asked for that still takes a
    // row of pixels is worse than no header, and "the space tabs are off" has
    // to look the same as it did before the slot existed.
    if (!spacesAsTabs || props.spaces.length === 0) return null
    return <SpaceTabs {...props} />
  },
}
